import { Alert, Button, Icon, LoadingSpinner, Tooltip } from "@oysterhr/ui"
import { useFileViewer } from "~/app/contexts/FileViewerContext"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useOffboardingAgreementActions,
  ToSAgreementDraftedAlert,
  DisableOffboardingESignModal,
} from "@oysterhr/offboarding"
import {
  AgreementAutomationStateEnum,
  AgreementStateEnum,
  AgreementTypeEnum,
  Employment,
  Engagement,
  EnvelopeEventTypeEnum,
  EngagementState,
  EnvelopeStateEnum,
} from "@oysterhr/schema"
import { UploadAgreementModal } from "./UploadAgreementModal"
import { ActivationModal } from "./ActivationModal"
import { SendReminderModal } from "./SendReminderModal"
import { DisableESignatureModal } from "./DisableESignatureModal"
import { ElectronicSignatureDisplay } from "./EletronicSignatureDisplay"
import { useAgreementSignatory } from "./AgreementSignerModal"
import { getSignatoryData } from "./AgreementSignerModal/utils"
import { AgreementsOverviewDrawer } from "./AgreementOverviewDrawer"
import { AgreementsWarningActionsAlert } from "./AgreementsWarningActionsAlert"
import { AgreementsCustomerNeedsToSign } from "./AgreementsCustomerNeedsToSign"
import { AgreementTabSignatory } from "./AgreementSignerModal/AgreementTabSignatory"
import { ShareAgreementsCTA } from "./ShareAgreementsCTA"
import { AgreementsWereUpdatedAlert } from "./AgreementsWereUpdatedAlert"
import { AgreementsEnvelopeNeedUpdateAlert } from "./AgreementsEnvelopeNeedUpdateAlert"
import { AgreementsTable } from "./AgreementsTable"
import { VerifyAgreementModal } from "./VerifyAgreementModal"
import { ShareAgreementsSuccessModal } from "./ShareAgreementsSuccessModal"
import { useMarkAgreementAsViewed } from "../hooks/useMarkAgreementAsViewed"
import { useAgreementsPolling } from "../hooks/useAgreementsPolling"
import { EnvelopePollingProvider } from "./EnvelopePollingContext"
import { useBannerConditions } from "../hooks/useBannerConditions"
import { useStaffAgreementsTableActions } from "../hooks/useStaffAgreementsTableActions"
import { DisableESignOSAModal } from "./AgreementSignerModal/DisableESignOSAModal/DisableESignOSAModal"
import { UnsupportedAutomationBanner } from "./UnsupportedAutomationBanner/UnsupportedAutomationBanner"
import { AgreementsAreSignedAlert } from "./AgreementsAreSignedAlert"
import AgreementsStaffNeedsToSign from "./AgreementsStaffNeedsToSign"
import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"
import { AgreementDetailsFragment } from "../../../gql/graphql"
import { VoidEnvelopeButton } from "./VoidEnvelope/VoidEnvelopeButton"
import { useVoidEnvelope } from "../../employment-agreements/hooks/useVoidEnvelope"
import { VoidEnvelopeConfirmationModal } from "./VoidEnvelope/VoidEnvelopeConfirmationModal"

interface StaffTabAgreementsProps {
  employment: Employment
  engagement: Engagement
}

const EMPLOYMENT_AGREEMENTS_TYPES = [
  AgreementTypeEnum.EmploymentAgreement,
  AgreementTypeEnum.RemoteWork,
  AgreementTypeEnum.ConfidentialityAndIntellectualProperty,
  AgreementTypeEnum.RestrictiveCovenant,
]

export const StaffTabAgreements = ({ employment, engagement }: StaffTabAgreementsProps) => {
  if (!employment.hiring)
    throw new Error("Employment must have a hiring (StaffTabAgreements - staff view)")
  // Hooks
  const [t] = useTranslation("Agreements")
  const { setViewObject } = useFileViewer()
  const markAgreementAsViewed = useMarkAgreementAsViewed()
  const { aspectType } = useAccountAspect()

  // Local state
  const [verifyAgreement, setVerifyAgreement] = useState<{
    show: boolean
    agreement?: AgreementDetailsFragment
  }>({ show: false, agreement: undefined })
  const [reminderModal, setReminderModal] = useState<{
    show: boolean
    agreement?: AgreementDetailsFragment
  }>({ show: false, agreement: undefined })

  const [agreementActivation, setAgreementActivation] = useState<{
    show: boolean
    agreement?: AgreementDetailsFragment
    action?: "enable" | "disable"
  }>({ show: false, agreement: undefined, action: undefined })

  const [disableAgreementESignModal, setDisableAgreementESignModal] = useState<{
    show: boolean
    agreement?: AgreementDetailsFragment
  }>({ show: false })

  const [disableOSAESignModal, setDisableOSAESignModal] = useState<{
    show: boolean
    agreement?: AgreementDetailsFragment
  }>({ show: false })

  const [disableOffboardingESignModal, setDisableOffboardingESignModal] = useState<{
    show: boolean
    agreement?: AgreementDetailsFragment
  }>({ show: false })

  const [showShareAgreementsSuccessModal, setShowShareAgreementsSuccessModal] = useState(false)

  // GraphQL
  const { agreements, disabledAgreements, loading, startPolling, terminationAgreements } =
    useAgreementsPolling({
      fetchDisabledAgreements: true,
    })

  const { data: agreementSignatoryData } = useAgreementSignatory(true, {
    variables: {
      engagementId: employment.id,
    },
  })

  const { showWarningAlertBanner } = useBannerConditions({ employment, agreements, aspectType })

  // Query data
  const signatoryEmploymentData = agreementSignatoryData?.employment
  const signatoryData = signatoryEmploymentData
    ? getSignatoryData(signatoryEmploymentData)
    : undefined

  useEffect(() => {
    startPolling(employment.id)
  }, [startPolling, employment.id])

  // Local state
  const [showUploadAgreementsModal, setShowUploadAgreementsModal] = useState(false)

  const openAgreementFileViewer = useCallback(
    (data: AgreementDetailsFragment) => {
      setViewObject({
        agreement: data,
        customDrawer: (
          <AgreementsOverviewDrawer
            agreement={data}
            employment={employment}
            showAdjustAgreementButton={false}
          />
        ),
      })

      if (employment.hiring && !data.lastUpdate?.viewedByMe) {
        markAgreementAsViewed({
          variables: {
            input: {
              hiringId: employment.hiring.id,
              agreementType: data.agreementType,
            },
          },
        })
      }
    },
    [setViewObject, employment, markAgreementAsViewed],
  )

  const hiringAndTerminationAgreements = useMemo(() => {
    return agreements.concat(terminationAgreements)
  }, [agreements, terminationAgreements])

  const agreementsAvailableForUpload = useMemo(() => {
    return employment.state === EngagementState.Terminated.toLowerCase()
      ? terminationAgreements
      : agreements
  }, [employment.state, agreements, terminationAgreements])

  const agreementsAvailableForUploadHaveAtLeastOneSigner = useMemo(() => {
    return agreementsAvailableForUpload.every(
      agg =>
        agg.envelope &&
        (agg.envelope.state === EnvelopeStateEnum.Sent ||
          agg.envelope.state === EnvelopeStateEnum.Pending) &&
        agg.envelope.events.some(evt => evt.eventType === EnvelopeEventTypeEnum.RecipientSigned),
    )
  }, [agreementsAvailableForUpload])

  const agreementsAvailableForUploadHaveEsign = useMemo(
    () => agreementsAvailableForUpload.every(agg => agg.isESignEnabled),
    [agreementsAvailableForUpload],
  )

  const agreementsWithESignEnabled = useMemo(
    () =>
      agreementsAvailableForUpload.filter(
        agg =>
          agg.isESignEnabled &&
          EMPLOYMENT_AGREEMENTS_TYPES.includes(agg.agreementType) &&
          agg.state !== AgreementStateEnum.Signed,
      ),
    [agreementsAvailableForUpload],
  )

  const {
    hasVoidableEnvelopeState,
    agreementsInEnvelope,
    isLoading: loadingVoidMutation,
    showVoidEnvelopeModal,
    openVoidEnvelopeModal,
    closeVoidEnvelopeModal,
    handleVoidEnvelope,
  } = useVoidEnvelope(agreementsWithESignEnabled, employment.id)

  const { generateOffboardingRowActions } = useOffboardingAgreementActions({
    employment: employment,
    setVerifyAgreement: setVerifyAgreement,
    setDisableOffboardingESignModal: setDisableOffboardingESignModal,
  })
  const generateHiringRowActions = useStaffAgreementsTableActions({
    employment,
    agreements,
    setReminderModal,
    setVerifyAgreement,
    setDisableOSAESignModal,
    setDisableAgreementESignModal,
    setAgreementActivation,
  })

  const generateRowActions = (agreement: AgreementDetailsFragment) =>
    agreement.agreementType === AgreementTypeEnum.TerminationOfServiceAgreement
      ? generateOffboardingRowActions(agreement)
      : generateHiringRowActions(agreement)

  const hasFailedAgreements = useMemo(
    () => agreements.some(agg => agg.automation?.state === AgreementAutomationStateEnum.Failed),
    [agreements],
  )

  const isRegeneration = agreements.some(agg => agg.lastUpdate && agg.isAutomated)

  return (
    <EnvelopePollingProvider employmentId={employment.id}>
      <main className="px-8 py-6">
        {/* Header */}
        <div
          className="mb-6 flex flex-row items-center justify-between"
          data-testid="staff-agreements-tab-header"
        >
          <div className="flex flex-row space-x-4">
            <Tooltip
              placement="top"
              value={
                agreementsAvailableForUploadHaveEsign &&
                agreementsAvailableForUploadHaveAtLeastOneSigner &&
                t("uploadAgreementTooltip")
              }
            >
              {/* We need this div here because the disabled prop of the button removes all hover effects. This is a "hacky" solution */}
              <div>
                <Button
                  variation="secondary"
                  onClick={() => {
                    setShowUploadAgreementsModal(true)
                  }}
                  disabled={
                    agreementsAvailableForUploadHaveEsign &&
                    agreementsAvailableForUploadHaveAtLeastOneSigner
                  }
                  className="w-fit"
                  startAdornment={<Icon name="FileArrowUp" />}
                >
                  <span>{t("uploadAgreement")}</span>
                </Button>
              </div>
            </Tooltip>
            {agreementsWithESignEnabled.length > 0 && (
              <VoidEnvelopeButton
                onClick={openVoidEnvelopeModal}
                isLoading={loadingVoidMutation}
                disabled={!hasVoidableEnvelopeState || loadingVoidMutation}
              />
            )}
          </div>
          <ElectronicSignatureDisplay agreements={hiringAndTerminationAgreements} />
        </div>
        {/* Failed agreements alert */}
        {hasFailedAgreements && (
          <Alert className="mb-5" variation="error">
            <Alert.Icon />
            <Alert.Heading>
              {isRegeneration ? "Could not regenerate agreements" : "Could not generate agreements"}
            </Alert.Heading>
            <Alert.Content>
              {isRegeneration
                ? "A previous attempt to regenerate some agreements below were failed. Please try again"
                : "A previous attempt to generate some agreements below were failed. Please try again"}
            </Alert.Content>
          </Alert>
        )}
        {/* Warning actions alert */}
        {showWarningAlertBanner && (
          <AgreementsWarningActionsAlert
            agreements={agreements}
            employment={employment}
            aspectType={aspectType}
          />
        )}

        {/* Visibility is handled inside */}
        <ToSAgreementDraftedAlert agreements={terminationAgreements} />

        {/* The visibility is calculated inside so we don't need to duplicate the logi */}
        <AgreementsCustomerNeedsToSign employment={employment} />

        {/* Visibility is done inside the CTA so we don't need to duplicate it between staff and customer */}
        <ShareAgreementsCTA
          openSuccessModal={() => setShowShareAgreementsSuccessModal(true)}
          agreements={agreements}
          employment={employment}
          className="mb-6"
          aspectType={aspectType}
        />

        {/* Visibility is done inside the alert so we don't need to duplicate it between staff and customer */}
        <AgreementsWereUpdatedAlert agreements={agreements} employment={employment} />

        {/* The visibility is calculated inside so we don't need to duplicate the logic */}
        <AgreementsEnvelopeNeedUpdateAlert
          employment={employment}
          agreements={agreements}
          className="mb-6"
          openSuccessModal={() => setShowShareAgreementsSuccessModal(true)}
        />

        {/* Visibility is handled inside as they're shared between staff and customer views */}
        <AgreementsAreSignedAlert employment={employment} agreements={agreements} />

        {/* Visibility is handled inside as they're shared between staff and customer views */}
        <UnsupportedAutomationBanner employment={employment} />
        <AgreementsStaffNeedsToSign employment={employment} />
        <AgreementTabSignatory
          employment={employment}
          agreements={terminationAgreements.concat(agreements)}
        />

        {loading ? (
          <LoadingSpinner />
        ) : (
          <AgreementsTable
            // We only want to display the loading state if there are no agreements, and it's loading
            // For example, on the first fetch
            agreements={terminationAgreements.concat(agreements).concat(disabledAgreements)}
            generateRowActions={generateRowActions}
            onAgreementClick={openAgreementFileViewer}
            isAdminView={true}
          />
        )}

        {showUploadAgreementsModal && employment.hiring && (
          <UploadAgreementModal
            agreements={agreementsAvailableForUpload}
            setShowModal={() => setShowUploadAgreementsModal(false)}
            employment={employment}
            isOpen={showUploadAgreementsModal}
          />
        )}

        {verifyAgreement.show && verifyAgreement.agreement && signatoryData && (
          <VerifyAgreementModal
            employment={employment}
            signatoryData={signatoryData}
            onRequestClose={() => setVerifyAgreement({ show: false, agreement: undefined })}
            agreement={verifyAgreement.agreement}
            isStaff={true}
          />
        )}
        {reminderModal.show && reminderModal.agreement && (
          <SendReminderModal
            employmentId={employment.id}
            agreement={reminderModal.agreement}
            closeModal={() => setReminderModal({ show: false, agreement: undefined })}
          />
        )}
        {agreementActivation.show &&
          agreementActivation.agreement &&
          agreementActivation.action && (
            <ActivationModal
              employment={employment}
              closeModal={() =>
                setAgreementActivation({ show: false, agreement: undefined, action: undefined })
              }
              agreement={agreementActivation.agreement}
              action={agreementActivation.action}
              engagement={engagement}
              agreementTypeName={t(
                `agreementTypeEnum.${agreementActivation.agreement.agreementType}`,
                {
                  ns: "Common",
                },
              )}
            />
          )}

        {disableAgreementESignModal.show && disableAgreementESignModal.agreement && (
          <DisableESignatureModal
            closeModal={() => setDisableAgreementESignModal({ show: false, agreement: undefined })}
            agreement={disableAgreementESignModal.agreement}
          />
        )}

        {disableOSAESignModal.show && disableOSAESignModal.agreement && (
          <DisableESignOSAModal
            onClose={() => setDisableOSAESignModal({ show: false, agreement: undefined })}
            agreement={disableOSAESignModal.agreement}
            hiringId={employment.hiring?.id ?? ""}
          />
        )}

        {disableOffboardingESignModal.show && disableOffboardingESignModal.agreement && (
          <DisableOffboardingESignModal
            onClose={() => setDisableOffboardingESignModal({ show: false, agreement: undefined })}
            agreement={disableOffboardingESignModal.agreement}
          />
        )}

        {showShareAgreementsSuccessModal && (
          <ShareAgreementsSuccessModal
            isSuccessModalOpen={showShareAgreementsSuccessModal}
            employment={employment}
            agreements={agreements}
            setShowSuccessModal={setShowShareAgreementsSuccessModal}
          />
        )}

        <VoidEnvelopeConfirmationModal
          agreements={agreementsInEnvelope}
          isOpen={showVoidEnvelopeModal}
          loading={loadingVoidMutation}
          onConfirm={handleVoidEnvelope}
          onClose={closeVoidEnvelopeModal}
        />
      </main>
    </EnvelopePollingProvider>
  )
}
